import type { DaySummary, Entry, Schemas } from "../api/client";
import type { QueuedOp, SettledOp } from "./types";

type Totals = Schemas["Totals"];
const NUTRIENTS: (keyof Totals)[] = [
  "kcal",
  "protein_g",
  "carbs_g",
  "fat_g",
  "fiber_g",
  "sugar_g",
  "saturated_fat_g",
  "sodium_mg",
];

function sum(entries: Entry[]): Totals {
  const t = Object.fromEntries(NUTRIENTS.map((k) => [k, 0])) as Totals;
  for (const e of entries) for (const k of NUTRIENTS) t[k] = (t[k] ?? 0) + (e[k] ?? 0);
  for (const k of NUTRIENTS) t[k] = Math.round((t[k] ?? 0) * 10) / 10;
  return t;
}

/**
 * A day as the server last sent it, plus this device's changes it may not know about yet:
 * everything still pending, and anything sent after that data was fetched.
 */
export function applyPending(
  s: DaySummary,
  pending: QueuedOp[],
  settled: SettledOp[],
  dataUpdatedAt: number,
): DaySummary {
  const ops = [...settled.filter((o) => o.syncedAt > dataUpdatedAt), ...pending].sort(
    (a, b) => a.seq - b.seq,
  );
  if (ops.length === 0) return s;

  // Work on a map of entry id -> entry, keeping server order.
  const entries = new Map<string, Entry>();
  for (const m of Object.values(s.meals)) for (const e of m?.entries ?? []) entries.set(e.id, e);
  const metrics = { ...s.metrics };
  let touched = false;

  for (const op of ops) {
    switch (op.kind) {
      case "putEntry":
      case "createFoodAndLog": {
        const id = op.kind === "putEntry" ? op.id : op.entryId;
        if (entries.delete(id)) touched = true;
        if (op.preview.date === s.date) {
          entries.set(id, op.preview);
          touched = true;
        }
        break;
      }
      case "deleteEntry":
        if (entries.delete(op.id)) touched = true;
        break;
      case "increment":
        if (op.body.date === s.date && op.body.metric === "water_ml") {
          metrics.water_ml = Math.max(0, (metrics.water_ml ?? 0) + op.body.delta);
          touched = true;
        }
        break;
      case "setMetric":
        if (op.date === s.date) {
          // A removed manual value may uncover another source's; the next fetch shows it.
          if (op.value == null) delete metrics[op.metric];
          else metrics[op.metric] = op.value;
          touched = true;
        }
        break;
    }
  }
  if (!touched) return s;

  const all = [...entries.values()];
  const meals = Object.fromEntries(
    Object.keys(s.meals).map((meal) => {
      const es = all.filter((e) => e.meal === meal);
      return [meal, { totals: sum(es), entries: es }];
    }),
  ) as DaySummary["meals"];
  const totals = sum(all);
  return {
    ...s,
    meals,
    totals,
    remaining_kcal: s.goal ? Math.round((s.goal.kcal - totals.kcal) * 10) / 10 : null,
    metrics,
  };
}
